import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Scissors, Trash2 } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { api, ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

export interface PhotoStages {
  ORIGINAL?: { id: string; url: string; width: number | null; height: number | null };
  CLEAN?:    { id: string; url: string; width: number | null; height: number | null };
  FINAL?:    { id: string; url: string; width: number | null; height: number | null };
}
export interface SlotEntry {
  slotIndex: number;
  slotKey: string | null;
  label?: string;          // human-readable shot label from photo package
  stages: PhotoStages;
}

type StageKey = 'ORIGINAL' | 'CLEAN' | 'FINAL';

const STAGE_LABELS: Record<StageKey, string> = {
  ORIGINAL: 'Original',
  CLEAN: 'Cutout',
  FINAL: 'Final',
};

export function PhotoViewerDialog({
  open,
  onOpenChange,
  slots,
  carId,
  initialSlotIndex,
  canCutout,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  slots: SlotEntry[];
  carId: string;
  initialSlotIndex: number | null;
  /** Map of slotIndex → whether the slot is cutout-eligible (exterior-only). */
  canCutout: (slotKey: string | null) => boolean;
}) {
  const qc = useQueryClient();
  const [slotIdx, setSlotIdx] = useState<number>(0);
  const [stage, setStage] = useState<StageKey>('FINAL');

  // Map "current viewer index" → element in `slots`
  const currentSlot = slots[slotIdx];
  const availableStages = useMemo<StageKey[]>(() => {
    if (!currentSlot) return [];
    return (['ORIGINAL', 'CLEAN', 'FINAL'] as StageKey[]).filter((k) => currentSlot.stages[k]);
  }, [currentSlot]);

  // Sync `slotIdx` and `stage` when the dialog opens or slot list changes.
  useEffect(() => {
    if (!open || initialSlotIndex == null) return;
    const idx = slots.findIndex((s) => s.slotIndex === initialSlotIndex);
    setSlotIdx(idx >= 0 ? idx : 0);
  }, [open, initialSlotIndex, slots]);

  useEffect(() => {
    if (!currentSlot) return;
    // Pick the most informative stage that exists, prefer FINAL → CLEAN → ORIGINAL.
    const order: StageKey[] = ['FINAL', 'CLEAN', 'ORIGINAL'];
    const fallback = order.find((k) => currentSlot.stages[k]);
    if (fallback && !currentSlot.stages[stage]) setStage(fallback);
    else if (!availableStages.length) {/* nothing */}
  }, [slotIdx, currentSlot, availableStages, stage]);

  // Keyboard navigation: ←/→ for slots, 1/2/3 for stages.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'ArrowLeft')  { e.preventDefault(); setSlotIdx((i) => Math.max(0, i - 1)); }
      if (e.key === 'ArrowRight') { e.preventDefault(); setSlotIdx((i) => Math.min(slots.length - 1, i + 1)); }
      if (e.key === '1' && availableStages.includes('ORIGINAL')) setStage('ORIGINAL');
      if (e.key === '2' && availableStages.includes('CLEAN'))    setStage('CLEAN');
      if (e.key === '3' && availableStages.includes('FINAL'))    setStage('FINAL');
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, slots.length, availableStages]);

  const cutoutOne = useMutation({
    mutationFn: (photoId: string) =>
      api.post<{ jobId: string | null; alreadyDone: boolean }>(`/photos/${photoId}/cutout`),
    onSuccess: (r) => {
      toast.success(r.alreadyDone ? 'Already has a cutout.' : 'Cutout queued — refresh to see it.');
      qc.invalidateQueries({ queryKey: ['car', carId] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Could not queue cutout'),
  });

  const deletePhoto = useMutation({
    mutationFn: (photoId: string) => api.delete(`/photos/${photoId}`),
    onSuccess: () => {
      toast.success('Photo removed');
      qc.invalidateQueries({ queryKey: ['car', carId] });
      onOpenChange(false);
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Delete failed'),
  });

  if (!currentSlot) return null;
  const showImage = currentSlot.stages[stage] ?? currentSlot.stages[availableStages[0] as StageKey];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-3 pr-6">
            <div className="min-w-0">
              <div className="text-xs text-muted-foreground">
                Slot {String(currentSlot.slotIndex).padStart(2, '0')}
              </div>
              <div className="truncate text-base">
                {currentSlot.label ?? (currentSlot.slotKey ?? 'Photo').replace(/_/g, ' ')}
              </div>
            </div>
            <Badge variant="outline" className="shrink-0">
              {slotIdx + 1} / {slots.length}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        {/* Stage tabs */}
        {availableStages.length > 0 ? (
          <Tabs value={stage} onValueChange={(v) => setStage(v as StageKey)} className="space-y-3">
            <TabsList>
              {(['ORIGINAL', 'CLEAN', 'FINAL'] as StageKey[]).map((k) => (
                <TabsTrigger
                  key={k}
                  value={k}
                  disabled={!currentSlot.stages[k]}
                  title={!currentSlot.stages[k] ? `${STAGE_LABELS[k]} not available yet` : undefined}
                >
                  {STAGE_LABELS[k]}
                  {!currentSlot.stages[k] && <span className="ml-1 text-[10px] text-muted-foreground">·</span>}
                </TabsTrigger>
              ))}
            </TabsList>

            {/* One TabsContent for the active stage (avoids re-loading every image on tab change). */}
            <TabsContent value={stage} className="m-0">
              <div
                className={cn(
                  'flex max-h-[60vh] min-h-[300px] items-center justify-center overflow-hidden rounded-md bg-[linear-gradient(135deg,#1a2230_25%,transparent_25%,transparent_50%,#1a2230_50%,#1a2230_75%,transparent_75%)] bg-[length:14px_14px]',
                )}
              >
                {showImage ? (
                  <img
                    src={showImage.url}
                    alt={STAGE_LABELS[stage]}
                    className="max-h-[60vh] max-w-full object-contain"
                  />
                ) : (
                  <div className="p-12 text-sm text-muted-foreground">No image</div>
                )}
              </div>
              {showImage?.width && showImage?.height && (
                <div className="mt-2 text-center text-[11px] text-muted-foreground">
                  {showImage.width} × {showImage.height} px
                </div>
              )}
            </TabsContent>
          </Tabs>
        ) : (
          <div className="rounded-md border border-dashed border-border p-12 text-center text-sm text-muted-foreground">
            No photos in this slot.
          </div>
        )}

        {/* Slot nav + actions */}
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
          <div className="flex items-center gap-1">
            <Button
              size="icon"
              variant="outline"
              disabled={slotIdx === 0}
              onClick={() => setSlotIdx((i) => Math.max(0, i - 1))}
              title="Previous slot (←)"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="outline"
              disabled={slotIdx >= slots.length - 1}
              onClick={() => setSlotIdx((i) => Math.min(slots.length - 1, i + 1))}
              title="Next slot (→)"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {currentSlot.stages.ORIGINAL && !currentSlot.stages.CLEAN && canCutout(currentSlot.slotKey) && (
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                disabled={cutoutOne.isPending}
                onClick={() => cutoutOne.mutate(currentSlot.stages.ORIGINAL!.id)}
              >
                <Scissors className="h-4 w-4" />
                {cutoutOne.isPending ? 'Queueing…' : 'Remove background'}
              </Button>
            )}
            {currentSlot.stages.ORIGINAL && (
              <Button
                variant="outline"
                size="sm"
                className="gap-2 text-destructive hover:text-destructive"
                disabled={deletePhoto.isPending}
                onClick={() => {
                  if (confirm('Delete this photo from the slot? Original, cutout, and final will all be removed.'))
                    deletePhoto.mutate(currentSlot.stages.ORIGINAL!.id);
                }}
              >
                <Trash2 className="h-4 w-4" /> Delete
              </Button>
            )}
            <DialogClose asChild>
              <Button size="sm">Close</Button>
            </DialogClose>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
